import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import type {
  GovernanceFormValues,
  GovernanceFilterType,
  GovernanceAllocationMethod,
} from "../types/Governance";
import {
  useV2ListResourcesForAccountQuery,
  useV2ListPhasesForAccountQuery,
  useV2ListServiceCategorysForAccountQuery,
  useGetAccountQuery,
} from "@generated";
import { useSelector } from "react-redux";
import type { RootState } from "@reducers/rootReducer";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import DropdownApi from "@components/FormsV2/DropdownApi/DropdownApi";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import "./style.css";

const FILTER_TYPE_OPTIONS = [
  { value: "all_services", label: "All Services" },
  { value: "filter_by_lob", label: "Filter by Line of Business" },
  { value: "filter_by_phase", label: "Filter by Phase" },
];

const ALLOCATION_METHOD_OPTIONS = [
  { value: "prorate_phases_by_effort", label: "Prorate phases by effort" },
  { value: "select_phase", label: "Select phase" },
  { value: "select_category", label: "Select category" },
  { value: "project_management_phase", label: "Project management phase" },
];

export interface GovernanceFormProps {
  id: string;
  initialValues: GovernanceFormValues;
  onSubmit: (values: GovernanceFormValues) => void;
  isSubmitting?: boolean;
}

const toOption = (id: string | null, label: string | null) =>
  id == null
    ? { value: 0, label: "" }
    : { value: id, label: label ?? String(id) };

export const GovernanceForm = ({
  id,
  initialValues,
  onSubmit,
  isSubmitting: _isSubmitting,
}: GovernanceFormProps) => {
  const [values, setValues] = useState<GovernanceFormValues>(initialValues);
  const update = <K extends keyof GovernanceFormValues>(
    key: K,
    val: GovernanceFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: account } = useGetAccountQuery({ slug: accountSlug } as any, {
    skip: !accountSlug,
  });
  const categoryLabel =
    (account?.data?.attributes as any)?.["field-labels"]?.[
      "service_category"
    ] || "Category";

  const isFixed = values.calculationType === "fixed_hours";
  const isPercent = values.calculationType === "percent_of_total";
  const showFilterId = values.filterType !== "all_services" && isPercent;

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="adminFormSectionTitle">Description</div>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="gov-name">Description</Form.Label>
          <TextField
            label={null}
            id="gov-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <DropdownApi
            label="* Resource"
            value={toOption(values.resourceId, null)}
            onChange={(opt: any) =>
              update("resourceId", opt ? String(opt.value) : null)
            }
            getDataHook={useV2ListResourcesForAccountQuery}
            required
          />
        </div>
        <div className="col-sm-3 governanceToggleCell">
          <Form.Label>Is this governance required by all projects?</Form.Label>
          <GenericToggle
            key={`req-${initialValues.required}`}
            initialValueOfIsOn={values.required}
            onToggle={(v: boolean) => update("required", v)}
            onText="Required"
            offText="Optional"
            hasText
          />
        </div>
      </div>

      <div className="adminFormSectionTitle">Governance Effort</div>
      <div className="governanceEffortHelp">
        How should we calculate governance effort?
      </div>
      <div className="row governanceEffortCardRow">
        <div className="col-sm-6">
          <label
            className={`governanceEffortCard ${isPercent ? "selected" : ""}`}
          >
            <input
              type="radio"
              name="calc-type"
              value="percent_of_total"
              checked={isPercent}
              onChange={() => update("calculationType", "percent_of_total")}
              className="governanceEffortCardInput"
            />
            <span className="governanceEffortRadio" aria-hidden>
              {isPercent && <span className="governanceEffortRadioInner" />}
            </span>
            <span className="governanceEffortCardText">
              <span className="governanceEffortCardTitle">
                Calculate governance effort
              </span>
              <span className="governanceEffortCardDesc">
                Calculate governance effort as a percentage of hours
              </span>
            </span>
          </label>
        </div>
        <div className="col-sm-6">
          <label
            className={`governanceEffortCard ${isFixed ? "selected" : ""}`}
          >
            <input
              type="radio"
              name="calc-type"
              value="fixed_hours"
              checked={isFixed}
              onChange={() => update("calculationType", "fixed_hours")}
              className="governanceEffortCardInput"
            />
            <span className="governanceEffortRadio" aria-hidden>
              {isFixed && <span className="governanceEffortRadioInner" />}
            </span>
            <span className="governanceEffortCardText">
              <span className="governanceEffortCardTitle">
                Set fixed governance effort
              </span>
              <span className="governanceEffortCardDesc">
                Set governance effort as a fixed number of hours
              </span>
            </span>
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-6">
          {isPercent ? (
            <FormNumberFieldV2
              label="* Enter Governance Effort Percentage"
              id="gov-rate"
              placeholder="Enter percentage"
              value={values.rate == null ? "" : values.rate * 100}
              min={0}
              max={100}
              step={0.1}
              required
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                update(
                  "rate",
                  e.target.value === "" ? null : Number(e.target.value) / 100
                )
              }
            />
          ) : (
            <FormNumberFieldV2
              label="* Enter Fixed Governance Hours"
              id="gov-fixed-hours"
              placeholder="Enter hours"
              value={values.fixedHours ?? ""}
              min={0}
              step={0.01}
              required
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                update(
                  "fixedHours",
                  e.target.value === "" ? null : Number(e.target.value)
                )
              }
            />
          )}
        </div>
      </div>
      {isPercent && (
        <>
          <div className="row">
            <div className="col-sm-12">
              <RequiredReactSelect
                label="* Which Services Impact Governance"
                value={
                  FILTER_TYPE_OPTIONS.find(
                    (o) => o.value === values.filterType
                  ) ?? FILTER_TYPE_OPTIONS[0]
                }
                onChange={(opt: any) =>
                  update(
                    "filterType",
                    (opt?.value ?? "all_services") as GovernanceFilterType
                  )
                }
                options={FILTER_TYPE_OPTIONS}
              />
            </div>
          </div>
          {showFilterId && values.filterType === "filter_by_phase" && (
            <div className="row">
              <div className="col-sm-6">
                <DropdownApi
                  label="Select Phase"
                  value={toOption(
                    values.filterId == null ? null : String(values.filterId),
                    null
                  )}
                  onChange={(opt: any) =>
                    update("filterId", opt ? Number(opt.value) : null)
                  }
                  getDataHook={useV2ListPhasesForAccountQuery}
                />
              </div>
            </div>
          )}
          {showFilterId && values.filterType === "filter_by_lob" && (
            <div className="row">
              <div className="col-sm-6">
                <Form.Label>Select Category</Form.Label>
                <Form.Control
                  type="text"
                  disabled
                  value="API support pending"
                />
                <small className="text-warning">
                  No `/v2/sections` list hook yet — track as cross-team API gap.
                </small>
              </div>
            </div>
          )}
        </>
      )}

      <div className="adminFormSectionTitle">
        Assign Governance Effort to the Project
      </div>
      <div className="row">
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Allocate to Project Phases by"
            value={ALLOCATION_METHOD_OPTIONS.find(
              (o) => o.value === values.allocationMethod
            )}
            onChange={(opt: any) =>
              update(
                "allocationMethod",
                (opt?.value ?? null) as GovernanceAllocationMethod | null
              )
            }
            options={ALLOCATION_METHOD_OPTIONS}
            placeholder="Select…"
          />
        </div>
        {values.allocationMethod === "select_phase" && (
          <div className="col-sm-3">
            <DropdownApi
              label="Phase"
              value={toOption(values.phaseId, null)}
              onChange={(opt: any) =>
                update("phaseId", opt ? String(opt.value) : null)
              }
              getDataHook={useV2ListPhasesForAccountQuery}
            />
          </div>
        )}
        {values.allocationMethod === "select_category" && (
          <div className="col-sm-3">
            <DropdownApi
              label={`* ${categoryLabel}`}
              value={toOption(values.sectionId, null)}
              onChange={(opt: any) =>
                update("sectionId", opt ? String(opt.value) : null)
              }
              getDataHook={useV2ListServiceCategorysForAccountQuery}
              labelAttribute="nested-name"
              required
            />
          </div>
        )}
        <div className="col-sm-3 governanceToggleCell">
          <Form.Label>
            Assign effort to services or report separately?
          </Form.Label>
          <GenericToggle
            key={`aes-${initialValues.assignEffortToService}`}
            initialValueOfIsOn={values.assignEffortToService}
            onToggle={(v: boolean) => update("assignEffortToService", v)}
            onText="Assign to service"
            offText="Keep separate"
            hasText
          />
        </div>
      </div>
    </Form>
  );
};
